import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContent from './AuthContent';
import ActivityGraph from './ActivityGraph';
import ActivityDetail from './ActivityDetail';
import ActivityList from './ActivityList';
import GoalsEditor from './goals/GoalsEditor';
import UserSettings from './UserSettings';
import PlanWeekView from './PlanWeekView';
import PlanYearView from './PlanYearView';
import './AuthContentWrapper.css';

const VIEW = {
    MAIN: 'MAIN',
    GRAPH: 'GRAPH',
    ACTIVITY: 'ACTIVITY',
    GOALS: 'GOALS',
    SETTINGS: 'SETTINGS',
    PLAN: 'PLAN',
    // "/" while OnboardingRedirect works out where to send the user - no
    // page of its own, just the top bar until the redirect lands.
    LANDING: 'LANDING',
};

// A freshly-connected Strava import runs in the background for as long as
// it takes (see app.py's save_strava_registration) with no progress
// endpoint to poll, so this is a "give up telling them" cutoff rather than
// a real completion signal - long enough for a full history import, short
// enough not to keep claiming work is in progress well after it's done.
const STRAVA_IMPORT_BANNER_DURATION_MS = 10 * 60 * 1000;

const getStravaImportBannerRemainingMs = () => {
    const startedAt = Number(localStorage.getItem('stravaImportStartedAt'));
    if (!startedAt) return 0;
    return STRAVA_IMPORT_BANNER_DURATION_MS - (Date.now() - startedAt);
};

const AuthContentWrapper = ({ initialView = VIEW.MAIN }) => {
    const navigate = useNavigate();
    const { activityId, year } = useParams();
    // App.js remounts this component fresh (via key={initialView}) whenever
    // the view category actually changes via an outside navigate() call, so
    // this only needs to seed the initial value - no need to keep it synced
    // with the prop afterwards.
    const [view, setView] = useState(initialView);
    const [cameFromGoalsEditor, setCameFromGoalsEditor] = useState(false);
    const [showStravaImportBanner, setShowStravaImportBanner] = useState(
        () => getStravaImportBannerRemainingMs() > 0
    );

    useEffect(() => {
        if (!showStravaImportBanner) return undefined;
        const remainingMs = getStravaImportBannerRemainingMs();
        if (remainingMs <= 0) {
            setShowStravaImportBanner(false);
            return undefined;
        }
        const timer = setTimeout(() => setShowStravaImportBanner(false), remainingMs);
        return () => clearTimeout(timer);
    }, [showStravaImportBanner]);

    const dismissStravaImportBanner = () => {
        localStorage.removeItem('stravaImportStartedAt');
        setShowStravaImportBanner(false);
    };

    const handleEditGoalsClick = () => {
        if (view === VIEW.GOALS) {
            setCameFromGoalsEditor(true);
            setView(VIEW.MAIN);
            navigate('/goals');
        } else {
            setView(VIEW.GOALS);
            navigate('/edit');
        }
    };

    const handleOverviewClick = () => {
        setCameFromGoalsEditor(false);
        setView(VIEW.MAIN);
        // A dedicated path (not "/") so this never gets intercepted by
        // OnboardingRedirect, which only acts on a landing at the root URL.
        navigate('/goals');
    };

    const handleGraphClick = () => {
        setCameFromGoalsEditor(false);
        setView(VIEW.GRAPH);
        navigate('/graph');
    };

    const handleActivityClick = () => {
        setCameFromGoalsEditor(false);
        setView(VIEW.ACTIVITY);
        navigate('/activity');
    };

    const handlePlanClick = () => {
        setCameFromGoalsEditor(false);
        setView(VIEW.PLAN);
        navigate('/plan');
    };

    return (
        <div className="authenticated-shell">
            <header className="top-bar">
                <div className="top-bar-nav">
                    <button
                        onClick={handleOverviewClick}
                        className={`top-bar-button${view === VIEW.MAIN ? ' active' : ''}`}
                        aria-current={view === VIEW.MAIN ? 'page' : undefined}
                    >
                        <span aria-hidden="true">⌂</span> Goals
                    </button>
                    <button
                        onClick={handlePlanClick}
                        className={`top-bar-button${view === VIEW.PLAN ? ' active' : ''}`}
                        aria-current={view === VIEW.PLAN ? 'page' : undefined}
                    >
                        <span aria-hidden="true">▦</span> Plan
                    </button>
                    <button
                        onClick={handleGraphClick}
                        className={`top-bar-button${view === VIEW.GRAPH ? ' active' : ''}`}
                        aria-current={view === VIEW.GRAPH ? 'page' : undefined}
                    >
                        <span aria-hidden="true">⌁</span> Graph
                    </button>
                    <button
                        onClick={handleActivityClick}
                        className={`top-bar-button${view === VIEW.ACTIVITY ? ' active' : ''}`}
                        aria-current={view === VIEW.ACTIVITY ? 'page' : undefined}
                    >
                        <span aria-hidden="true">▤</span> Activity
                    </button>
                    <button
                        onClick={handleEditGoalsClick}
                        className={`top-bar-button${view === VIEW.GOALS ? ' active' : ''}`}
                        aria-current={view === VIEW.GOALS ? 'page' : undefined}
                    >
                        <span aria-hidden="true">✎</span> Edit
                    </button>
                </div>
            </header>
            {showStravaImportBanner && (
                <div className="strava-import-banner" role="status">
                    <span>
                        <span aria-hidden="true">⟳</span> Importing your Strava activities in the background - this can take a few minutes.
                    </span>
                    <button type="button" onClick={dismissStravaImportBanner} aria-label="Dismiss">×</button>
                </div>
            )}
            {view === VIEW.LANDING ? null : view === VIEW.SETTINGS ? (
                <UserSettings />
            ) : view === VIEW.GOALS ? (
                <GoalsEditor />
            ) : view === VIEW.GRAPH ? (
                <ActivityGraph />
            ) : view === VIEW.ACTIVITY ? (
                activityId ? <ActivityDetail /> : <ActivityList />
            ) : view === VIEW.PLAN ? (
                year ? <PlanYearView /> : <PlanWeekView />
            ) : (
                <AuthContent cameFromGoalsEditor={cameFromGoalsEditor} />
            )}
        </div>
    );
}
export default AuthContentWrapper;
